<main
  class="content crop-fields-content"
  data-test="fms-diagnose-crop-fields"
  [attr.aria-label]="'fmsDiagnose.tabs.cropFields.title' | translate"
>
  @if (cropFieldsState$ | async; as state) {
    <ng-template #refreshButton>
      <button
        mat-stroked-button
        class="sync-from-fms-button"
        type="button"
        [disabled]="state.kind === 'loading' || state.kind === 'syncing'"
        [matTooltip]="'fmsDiagnose.tabs.cropFields.refreshTooltip' | translate"
        (click)="requestRetry()"
        data-test="fms-diagnose-refresh-crop-fields"
      >
        <span class="sync-from-fms-button__content">
          @if (state.kind === 'syncing') {
            <mat-spinner diameter="18" strokeWidth="3"></mat-spinner>
          } @else {
            <img
              src="assets/icons/sync-from-fms.svg"
              alt=""
              aria-hidden="true"
              class="sync-from-fms-button__icon"
              height="18"
              width="22"
            />
          }
          <span class="sync-from-fms-button__text">
            {{ 'fmsDiagnose.tabs.cropFields.refresh' | translate }}
          </span>
        </span>
      </button>
    </ng-template>

    <ng-template #syncWarning>
      @if (state.kind === 'loaded' && state.data.state === 'ReadyWithWarning') {
        <div class="crop-fields-status-card" data-test="fms-diagnose-sync-warning">
          <mat-icon aria-hidden="true">warning</mat-icon>
          <div class="crop-fields-status-card__message">
            <h3>{{ 'fmsDiagnose.tabs.cropFields.syncFailedTitle' | translate }}</h3>
            <p>{{ 'fmsDiagnose.tabs.cropFields.syncFailedWarning' | translate }}</p>
          </div>
        </div>
      }
    </ng-template>

    <ng-template #topRightActions>
      <ng-container *ngTemplateOutlet="syncWarning"></ng-container>
      <div class="crop-fields-action-slot">
        <ng-container *ngTemplateOutlet="refreshButton"></ng-container>
      </div>
    </ng-template>

    @if (state.kind !== 'unavailable') {
      <div class="crop-fields-actions">
        @if (state.kind === 'loaded') {
          @let cropFields = state.data;
          @if (hasData(cropFields)) {
            @let linkedFieldCount = cropFields.linkedFields.length;
            @let internalOnlyFieldCount = cropFields.internalOnlyFields.length;
            @let providerOnlyFieldCount = cropFields.providerOnlyFields.length;
            @let cropFieldsWithoutMatch = internalOnlyFieldCount + providerOnlyFieldCount;
            @let allCropFieldsLinked = linkedFieldCount > 0 && cropFieldsWithoutMatch === 0;
            <div
              class="crop-fields-alert crop-fields-alert--full-width"
              [class.crop-fields-alert--success]="allCropFieldsLinked"
              [class.crop-fields-alert--warning]="!allCropFieldsLinked"
            >
              <output class="crop-fields-alert__message" aria-live="polite">
                <mat-icon aria-hidden="true">{{
                  allCropFieldsLinked ? 'check' : 'warning'
                }}</mat-icon>
                <div>
                  <h3>
                    {{ linkedFieldCount }}
                    {{ 'fmsDiagnose.tabs.cropFields.alert.title.linked' | translate }}
                    @if (cropFieldsWithoutMatch > 0) {
                      · {{ cropFieldsWithoutMatch }}
                      {{ 'fmsDiagnose.tabs.cropFields.alert.title.withoutMatch' | translate }}
                    }
                  </h3>
                  <p>{{ 'fmsDiagnose.tabs.cropFields.alert.subtitle' | translate }}</p>
                </div>
              </output>
              <div class="crop-fields-alert__actions">
                <ng-container *ngTemplateOutlet="refreshButton"></ng-container>
              </div>
            </div>
          } @else {
            <ng-container *ngTemplateOutlet="topRightActions"></ng-container>
          }
        } @else {
          <ng-container *ngTemplateOutlet="topRightActions"></ng-container>
        }
      </div>
    }
    @if (state.kind === 'loading') {
      <output class="crop-fields-state" aria-live="polite">
        <mat-spinner
          diameter="40"
          [attr.aria-label]="'fmsDiagnose.shared.loading' | translate"
        ></mat-spinner>
      </output>
    } @else if (state.kind === 'syncing') {
      <output class="crop-fields-state" aria-live="polite">
        <mat-spinner
          diameter="40"
          [attr.aria-label]="'fmsDiagnose.shared.loading' | translate"
        ></mat-spinner>
        <span>{{ 'fmsDiagnose.tabs.cropFields.syncing' | translate }}</span>
      </output>
    } @else if (state.kind === 'unavailable') {
      <output class="crop-fields-state">
        <mat-icon aria-hidden="true">link_off</mat-icon>
        <span>{{ 'fmsDiagnose.tabs.cropFields.unavailable' | translate }}</span>
      </output>
    } @else if (state.kind === 'error') {
      <output class="crop-fields-state" role="alert">
        <mat-icon aria-hidden="true">error</mat-icon>
        <span>{{ 'fmsDiagnose.shared.error' | translate }}</span>
        <button mat-stroked-button type="button" (click)="requestRetry()">
          {{ 'fmsDiagnose.shared.retry' | translate }}
        </button>
      </output>
    } @else if (state.kind === 'loaded') {
      @let cropFields = state.data;
      @if (hasData(cropFields)) {
        @if (
          cropFields.linkedFields.length > 0 ||
          cropFields.internalOnlyFields.length > 0 ||
          cropFields.providerOnlyFields.length > 0
        ) {
          @let allLinkedFields = cropFields.linkedFields;
          @let allProviderOnlyFields = cropFields.providerOnlyFields;
          <div class="crop-field-farm-layout">
            <section class="map-card" aria-labelledby="fms-diagnose-map-title">
              <div class="card-heading">
                <div>
                  <h3 id="fms-diagnose-map-title">
                    <mat-icon aria-hidden="true">map</mat-icon>
                    {{ 'fmsDiagnose.tabs.cropFields.mapComparison' | translate }}
                  </h3>
                </div>
                @if (state.kind === 'loaded' && state.data.state === 'ReadyWithWarning') {
                  <div class="map-card__header-warning">
                    <ng-container *ngTemplateOutlet="syncWarning"></ng-container>
                  </div>
                }
              </div>

              <app-fms-diagnose-map
                [ariaLabel]="'fmsDiagnose.tabs.cropFields.mapComparison' | translate"
                [internalOnlyFields]="cropFields.internalOnlyFields"
                [linkedFields]="cropFields.linkedFields"
                [providerOnlyFields]="cropFields.providerOnlyFields"
              ></app-fms-diagnose-map>

              <div class="map-legend">
                <span>
                  <i class="legend-swatch legend-swatch--linked"></i>
                  {{ 'fmsDiagnose.tabs.cropFields.legend.linked' | translate }}
                </span>
                <span>
                  <i class="legend-swatch legend-swatch--bms-overlap"></i>
                  {{ 'fmsDiagnose.tabs.cropFields.legend.bmsOverlap' | translate }}
                </span>
                <span>
                  <i class="legend-swatch legend-swatch--agrifirm-only"></i>
                  {{ 'fmsDiagnose.tabs.cropFields.legend.agrifirmOnly' | translate }}
                </span>
                <span>
                  <i class="legend-swatch legend-swatch--bms-only"></i>
                  {{ 'fmsDiagnose.tabs.cropFields.legend.bmsOnly' | translate }}
                </span>
              </div>
            </section>

            <section
              class="linkages-card"
              data-test="fms-diagnose-crop-field-status-linked"
              aria-labelledby="fms-diagnose-linkages-title"
            >
              <div class="card-heading">
                <div>
                  <h3 id="fms-diagnose-linkages-title">
                    <mat-icon aria-hidden="true">link</mat-icon>
                    {{ 'fmsDiagnose.tabs.cropFields.linkages' | translate }}
                  </h3>
                </div>
              </div>

              @if (allLinkedFields.length > 0) {
                <section class="field-status-group">
                  <header class="field-status-heading">
                    <div class="field-status-title-row">
                      <h4 data-test="fms-diagnose-crop-field-status-linked-title">
                        {{ 'fmsDiagnose.tabs.cropFields.linked.title' | translate }}
                      </h4>
                      <span class="count-badge count-badge--linked">{{
                        allLinkedFields.length
                      }}</span>
                    </div>
                  </header>
                  @for (field of allLinkedFields; track field.internalFarm.key) {
                    <article
                      class="crop-field-row crop-field-row--linked"
                      data-test="fms-diagnose-crop-field"
                    >
                      <div
                        class="crop-field-name-pair"
                        data-test="fms-diagnose-crop-field-name-pair"
                      >
                        <div class="crop-field-row-title">
                          <strong
                            class="crop-field-name"
                            data-test="fms-diagnose-internal-crop-field-name"
                          >
                            {{ fieldName(field.internalFarm) }}
                          </strong>
                          @if (fieldArea(field.internalFarm); as area) {
                            <strong
                              class="crop-field-area"
                              data-test="fms-diagnose-crop-field-size"
                            >
                              {{ area | number: '1.2-2' }} ha
                            </strong>
                          }
                        </div>
                        <div class="crop-field-external-name">
                          <mat-icon aria-hidden="true">subdirectory_arrow_right</mat-icon>
                          <span data-test="fms-diagnose-external-crop-field-name">
                            {{ fieldName(field.providerFarm) }}
                            ({{ 'fmsDiagnose.tabs.cropFields.externalName' | translate }})
                          </span>
                        </div>
                      </div>
                    </article>
                  }
                </section>
              }

              @if (cropFields.internalOnlyFields.length > 0) {
                <section
                  class="field-status-group"
                  data-test="fms-diagnose-crop-field-status-agrifirm-only"
                >
                  <header class="field-status-heading">
                    <div class="field-status-title-row">
                      <h4 data-test="fms-diagnose-crop-field-status-agrifirm-only-title">
                        {{ 'fmsDiagnose.tabs.cropFields.agrifirmOnly.title' | translate }}
                      </h4>
                      <span class="count-badge count-badge--agrifirm-only">{{
                        cropFields.internalOnlyFields.length
                      }}</span>
                    </div>
                    <span
                      class="field-status-subtitle"
                      data-test="fms-diagnose-crop-field-status-agrifirm-only-subtitle"
                    >
                      {{ 'fmsDiagnose.tabs.cropFields.agrifirmOnly.subtitle' | translate }}
                    </span>
                  </header>
                  @for (field of cropFields.internalOnlyFields; track field.key) {
                    <article
                      class="crop-field-row crop-field-row--agrifirm-only"
                      data-test="fms-diagnose-crop-field"
                    >
                      <div class="crop-field-row-main">
                        <div class="crop-field-row-title">
                          <strong class="crop-field-name">{{ fieldName(field) }}</strong>
                          @if (fieldArea(field); as area) {
                            <strong class="crop-field-area">
                              {{ area | number: '1.2-2' }} ha
                            </strong>
                          }
                        </div>
                      </div>
                    </article>
                  }
                </section>
              }

              @if (allProviderOnlyFields.length > 0) {
                <section
                  class="field-status-group"
                  data-test="fms-diagnose-crop-field-status-bms-only"
                >
                  <header class="field-status-heading">
                    <div class="field-status-title-row">
                      <h4 data-test="fms-diagnose-crop-field-status-bms-only-title">
                        {{ 'fmsDiagnose.tabs.cropFields.bmsOnly.title' | translate }}
                      </h4>
                      <span class="count-badge count-badge--bms-only">{{
                        allProviderOnlyFields.length
                      }}</span>
                    </div>
                    <span
                      class="field-status-subtitle"
                      data-test="fms-diagnose-crop-field-status-bms-only-subtitle"
                    >
                      {{ 'fmsDiagnose.tabs.cropFields.bmsOnly.subtitle' | translate }}
                    </span>
                  </header>
                  @for (field of allProviderOnlyFields; track field.key) {
                    <article
                      class="crop-field-row crop-field-row--bms-only"
                      data-test="fms-diagnose-crop-field"
                    >
                      <div class="crop-field-row-main">
                        <div class="crop-field-row-title">
                          <strong class="crop-field-name">{{ fieldName(field) }}</strong>
                          @if (fieldArea(field); as area) {
                            <strong class="crop-field-area">
                              {{ area | number: '1.2-2' }} ha
                            </strong>
                          }
                        </div>
                      </div>
                    </article>
                  }
                </section>
              }
            </section>
          </div>
        }
      } @else {
        <output class="crop-fields-state">
          <mat-icon aria-hidden="true">grid_off</mat-icon>
          {{ 'fmsDiagnose.tabs.cropFields.empty' | translate }}
        </output>
      }
    }
  }
</main>
